import type { AppProps } from "next/app";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { LoginSessionWatcher } from "src/domains/shared/components/LoginSessionWatcher";
import { useBindLoginSupersededHandler } from "src/domains/shared/hooks/useBindLoginSupersededHandler";
import { bootExclusiveLogin } from "src/domains/shared/utils/exclusiveLogin/bootExclusiveLogin";
import { installStaleBuildReload } from "src/domains/shared/utils/staleBuildReload";
import { ReviewModeIndicator } from "src/domains/shared/components/ReviewModeIndicator";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Amplify } from "aws-amplify";
import dynamic from "next/dynamic";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import GlobalStyle from "../styles/GlobalStyle";
import { amplifyConfig } from "../awsconfig";
import "../../src/domains/shared/constants/locale/i18n";
import Script from "next/script";
import Head from "next/head";
import * as gtag from "../../src/domains/shared/utils/gtag";
import { Provider } from "jotai";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider } from "@mui/material/styles";
import defaultTheme from "../../src/domains/shared/constants/styles/themeProvider";
import "ol/ol.css";

dayjs.extend(utc);
dayjs.extend(timezone);

// Cesium baseUrl 설정 (클라이언트 사이드에서만)
// webpack DefinePlugin으로 주입된 CESIUM_BASE_URL을 전역 변수로 설정
if (typeof window !== "undefined") {
  // CESIUM_BASE_URL은 webpack DefinePlugin으로 주입됨
  // @ts-ignore - webpack DefinePlugin으로 주입된 전역 변수
  const cesiumBaseUrl = (
    typeof (globalThis as any).CESIUM_BASE_URL !== "undefined"
      ? (globalThis as any).CESIUM_BASE_URL
      : "/build/cesium"
  ) as string;
  (window as any).CESIUM_BASE_URL = cesiumBaseUrl;
}

// ToastContainer를 동적으로 import하여 클라이언트 사이드에서만 로드
const ToastContainer = dynamic(
  () => import("react-toastify").then((mod) => mod.ToastContainer),
  { ssr: false }
);


export const queryClient = new QueryClient();

if (typeof window !== "undefined") {
  Amplify.configure(amplifyConfig);
  // 로그인 배타성: 첫 백엔드 요청 전에 fetch 관문(밀려남 감지, 점유 대기·밀려남 중 백엔드 요청 차단)을 설치하고,
  // 렌더(로그인 판정) 전에 남아 있는 점유 대기를 취소한다 — 이전 로드가 버린 점유와 다른 탭에서 확인창이 떠 있는
  // 점유를 가리지 못해 둘 다 취소한다(후자의 탭은 확인이 실패로 끝난다)
  bootExclusiveLogin();
  // 배포 전에 연 탭이 지워진 옛 지연 청크를 부르다 빈 화면에 멈추지 않게, 배포가 확인되면 새로 고친다
  installStaleBuildReload();
}

function MyApp({ Component, pageProps: { session, ...pageProps } }: AppProps) {
  const router = useRouter();
  // 로그인 배타성: 밀려남 처리기에 라우터 이동·쿼리 캐시 비우기를 주입한다. 어느 감지 경로(관문·감시자·탭 신호)로
  // 시작된 처리든 이 주입이 있어야 종착지(로그인 화면, 로그아웃 뒤면 홈)로 옮긴다
  useBindLoginSupersededHandler(queryClient);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }
    const handleRouteChange = (url: URL) => {
      gtag.pageview(url);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("hashChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("hashChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <Provider>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <ThemeProvider theme={defaultTheme}>
            <GlobalStyle />
            <Head>
              <title>DroneSquare</title>
              <meta
                name="description"
                content="드론 기반 인프라 자산관리 플랫폼"
              ></meta>
              <meta name="title" content="DroneSquare" />
              <meta
                name="keywords"
                content="DroneSquare, 드론스퀘어, 드론, Drone, 설계, 감리, 공간 데이터, 스마트시티, 빅데이터, BIM, 정사영상, 포인트클라우드, 메쉬, mesh, pointcloud"
              />
              <meta name="author" content="DroneSquare"></meta>
            </Head>
            {process.env.NODE_ENV === "production" && (
              <>
                <Script
                  strategy="afterInteractive"
                  src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
                />
                <Script
                  id="gtag-init"
                  strategy="afterInteractive"
                  dangerouslySetInnerHTML={{
                    __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${gtag.GA_TRACKING_ID}', {
                      page_path: window.location.pathname,
                    });
                  `,
                  }}
                />
              </>
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <LoginSessionWatcher />
              <ReviewModeIndicator />
              <Component {...pageProps} />
            </LocalizationProvider>
            <ReactQueryDevtools initialIsOpen={false} position="top-left" />
            <ToastContainer position="top-right" hideProgressBar={true} />
          </ThemeProvider>
        </Hydrate>
      </QueryClientProvider>
    </Provider>
  );
}

export default MyApp;
